// 简单的投资分析引擎占位
// 提供 NPV、IRR、折旧（直线法）、蒙特卡洛敏感性分析占位实现

// NPV: cashFlows 为数组，cashFlows[0] 通常为初始投资（负数），r 为年折现率（小数）
function calculateNPV(cashFlows, r) {
  return cashFlows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + r, i), 0)
}

// IRR: 使用二分法/牛顿法求解 X 使得 NPV 为零；这里用二分（年化利率区间）
function calculateIRR(cashFlows, { tol = 1e-6, maxIter = 200, low = -0.9999, high = 10 } = {}) {
  const npv = (rate) => cashFlows.reduce((s, cf, i) => s + cf / Math.pow(1 + rate, i), 0)
  let lo = low
  let hi = high
  let fLo = npv(lo)
  let fHi = npv(hi)
  if (fLo * fHi > 0) {
    return NaN
  }
  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2
    const fMid = npv(mid)
    if (Math.abs(fMid) < tol) return mid
    if (fLo * fMid <= 0) {
      hi = mid
      fHi = fMid
    } else {
      lo = mid
      fLo = fMid
    }
  }
  return (lo + hi) / 2
}

// Straight-line depreciation: (cost - salvage) / lifeYears per year
function straightLineDepreciation(cost, salvage, lifeYears) {
  if (lifeYears <= 0) return []
  const yearly = (cost - salvage) / lifeYears
  const arr = []
  for (let y = 1; y <= lifeYears; y++) {
    arr.push({ year: y, depreciation: yearly, accumulated: yearly * y })
  }
  return arr
}

// Double-declining balance depreciation: double rate on book value, stop at salvage
function doubleDecliningDepreciation(cost, salvage, lifeYears) {
  if (lifeYears <= 0) return []
  const rate = 2 / lifeYears
  let book = cost
  const arr = []
  let accumulated = 0
  for (let y = 1; y <= lifeYears; y++) {
    let dep = book * rate
    // ensure we do not depreciate below salvage
    if (book - dep < salvage) dep = book - salvage
    accumulated += dep
    arr.push({ year: y, depreciation: dep, accumulated })
    book -= dep
  }
  return arr
}

// Monte Carlo sensitivity: accept a function that maps random draws to NPV, repeat N times
function monteCarloNPV(simulatorFunc, draws = 1000) {
  const results = []
  for (let i = 0; i < draws; i++) {
    results.push(simulatorFunc())
  }
  results.sort((a,b)=>a-b)
  const mean = results.reduce((s,v)=>s+v,0)/results.length
  const p10 = results[Math.floor(0.1*results.length)]
  const p90 = results[Math.floor(0.9*results.length)]
  return { mean, p10, p90, samples: results }
}

export { calculateNPV, calculateIRR, straightLineDepreciation, doubleDecliningDepreciation, monteCarloNPV }

// 评估一个简单资本项目：计算每年净利润、经营性现金流、净现金流序列、NPV 和 IRR
// 参数示例对象：
// {
//   cost: 2800000, // 设备成本
//   lifeYears: 7,
//   salvage: 0,
//   annualSalesUnits: 4000,
//   pricePerUnit: 5000,
//   fixedCost: 3100000, // 不含折旧
//   variableCostPerUnit: 3750,
//   workingCapital: 2200000, // 期初投入
//   taxRate: 0.4,
//   discountRate: 0.15
// }
function evaluateProject(params) {
  const {
    cost,
    lifeYears,
    // 会计折旧使用的残值（账面残值）
    salvage = 0,
    // 期末实际回收值（用户可配置），默认等于会计残值；仅用于期末现金流的回收入账
    terminalRecovery = salvage,
    depreciationMethod = 'straight', // 'straight' or 'double'
    annualSalesUnits,
    pricePerUnit,
    fixedCost,
    variableCostPerUnit,
    workingCapital = 0,
    taxRate = 0,
    discountRate = 0
  } = params

  // 生成折旧表
  let depSchedule = []
  if (depreciationMethod === 'double') {
    depSchedule = doubleDecliningDepreciation(cost, salvage, lifeYears)
  } else {
    depSchedule = straightLineDepreciation(cost, salvage, lifeYears)
  }

  const yearlyDep = depSchedule.map(d => d.depreciation)

  const annualRevenues = annualSalesUnits * pricePerUnit
  const annualVariableCost = annualSalesUnits * variableCostPerUnit
  const ebitList = []
  const taxableIncomeList = []
  const netProfitList = []
  const operatingCashFlowList = []

  for (let y = 1; y <= lifeYears; y++) {
    const dep = yearlyDep[y - 1] || 0
    const ebit = annualRevenues - fixedCost - annualVariableCost - dep // 息税前利润
    const taxable = ebit // 简化：无利息费用
    const tax = Math.max(0, taxable * taxRate)
    const netProfit = taxable - tax
    const ocf = netProfit + dep // 经营性现金流：净利润 + 折旧（非现金）

    ebitList.push(ebit)
    taxableIncomeList.push(taxable)
    netProfitList.push(netProfit)
    operatingCashFlowList.push(ocf)
  }

  // 净现金流序列（含期初投资与营运资本）：
  // t=0: -cost - workingCapital
  // t=1..lifeYears-1: operatingCashFlow
  // t=lifeYears: operatingCashFlow + salvage + workingCapital回收
  const cashFlows = []
  cashFlows.push(-(cost || 0) - (workingCapital || 0))
  for (let i = 0; i < lifeYears; i++) {
    const ocf = operatingCashFlowList[i] || 0
    // 最后一年加上残值与营运资本回收
    // 最后一年加上期末实际回收值（terminalRecovery）与营运资本回收
    if (i === lifeYears - 1) {
      cashFlows.push(ocf + (terminalRecovery || 0) + (workingCapital || 0))
    } else {
      cashFlows.push(ocf)
    }
  }

  const npv = calculateNPV(cashFlows, discountRate)
  const irr = calculateIRR(cashFlows)
  const paybackPeriod = (function calcPayback(flows){
    // 简单投资回收期（未折现）：以 t=0 为初始投资（通常为负），从 t=1 开始累计正向现金流直至覆盖初始投资
    if (!Array.isArray(flows) || flows.length === 0) return NaN
    const initial = -(Number(flows[0]) || 0)
    if (initial <= 0) return 0
    let remaining = initial
    for (let t = 1; t < flows.length; t++) {
      const cf = Number(flows[t]) || 0
      if (cf <= 0) continue
      if (cf >= remaining) {
        return (t - 1) + remaining / cf
      }
      remaining -= cf
    }
    return NaN // 未能在寿命内收回
  })(cashFlows)

  return {
    depreciationSchedule: depSchedule,
    annualRevenues,
    annualVariableCost,
    ebitList,
    taxableIncomeList,
    netProfitList,
    operatingCashFlowList,
    cashFlows,
    npv,
    irr,
    paybackPeriod,
    // 把用户用于期末回收的实际回收值回传，便于 UI/导出一致性校验
    terminalRecovery
  }
}

export { evaluateProject }
